import { Avatar as BaseAvatar } from "@base-ui/react/avatar";

import { tv } from "../foundations/cn.js";

const avatarStyles = tv({
  base: "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-pill bg-bg-raised font-body text-text-secondary select-none",
  variants: {
    size: {
      xs: "size-5 text-[9px]",
      sm: "size-7 text-body-b3-strong",
      md: "size-10 text-body-b1-strong",
      lg: "size-16 text-heading-h1",
    },
  },
  defaultVariants: { size: "md" },
});

export interface AvatarProps {
  /** The picture; the initials show until it loads, or if it fails. */
  src?: string | null;
  /** The person's display name; the initials come from it. */
  name: string;
  size?: "xs" | "sm" | "md" | "lg";
  /** True when the name is already visible beside it, so it isn't read twice. */
  decorative?: boolean;
  className?: string;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase() || "?";
}

/** A person's picture, falling back to their initials. */
export function Avatar({ src, name, size, decorative = false, className }: AvatarProps) {
  return (
    <BaseAvatar.Root
      className={avatarStyles({ size, className })}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
    >
      {src && <BaseAvatar.Image src={src} alt="" className="size-full object-cover" />}
      {decorative ? (
        // Drawn by CSS, not as text: a decorative avatar sits beside the name it stands for, so
        // its letters must not join that name's visible text (WCAG 2.5.3, label in name).
        <BaseAvatar.Fallback
          aria-hidden
          data-initials={initials(name)}
          className="before:content-[attr(data-initials)]"
        />
      ) : (
        <BaseAvatar.Fallback aria-hidden>{initials(name)}</BaseAvatar.Fallback>
      )}
    </BaseAvatar.Root>
  );
}
